import { useEffect, useState, useMemo } from "react";


/* ── Theme detection (runs before React context) ── */
function detectDark(): boolean {
  try {
    const stored = localStorage.getItem("theme");
    if (stored === "dark") return true;
    if (stored === "light") return false;
  } catch {}
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/* ── Palette ── */
const DARK = {
  bg: "#0A0E1A",
  logoGradient: "linear-gradient(135deg, #9333EA, #A78BFA, #E0CCFF)",
  tagline: "rgba(255,255,255,0.45)",
  primary: "#7C3AED",
  accent: "#A78BFA",
  aurora1: "hsl(252 80% 55% / 0.25)",
  aurora1b: "hsl(280 70% 50% / 0.12)",
  aurora2: "hsl(260 90% 50% / 0.2)",
  aurora2b: "hsl(230 80% 45% / 0.08)",
};

const LIGHT = {
  bg: "#FAFAFF",
  logoGradient: "linear-gradient(135deg, #4F46E5, #7C3AED, #A855F7)",
  tagline: "hsl(252 10% 45%)",
  primary: "#7C3AED",
  accent: "#6D28D9",
  aurora1: "hsl(252 80% 70% / 0.12)",
  aurora1b: "hsl(280 60% 70% / 0.06)",
  aurora2: "hsl(260 70% 65% / 0.1)",
  aurora2b: "hsl(230 60% 60% / 0.05)",
};

const LOGO_LETTERS = ["A", "C", "E", "X"];

/* ── Keyframes ── */
const SPLASH_STYLES = `
@keyframes splashAurora1 {
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(50px, -40px) scale(1.12); }
  100% { transform: translate(0, 0) scale(1); }
}
@keyframes splashAurora2 {
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-40px, 30px) scale(1.08); }
  100% { transform: translate(0, 0) scale(1); }
}
@keyframes splashWordmarkIn {
  0%   { opacity: 0; transform: translateY(14px) scale(0.92); filter: blur(4px); }
  60%  { opacity: 1; transform: translateY(-2px) scale(1.01); filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
}
@keyframes splashLetterIn {
  0%   { opacity: 0; transform: translateY(18px) scale(0.85); filter: blur(6px); }
  60%  { opacity: 1; transform: translateY(-3px) scale(1.02); filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
}
@keyframes splashTaglineIn {
  0%   { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes splashLoaderFadeIn {
  0%   { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes splashLoaderPulse {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50%      { transform: translate(-50%, -50%) scale(1.06); }
}
@media (prefers-reduced-motion: reduce) {
  @keyframes splashAurora1  { 0%, 100% { transform: none; } }
  @keyframes splashAurora2  { 0%, 100% { transform: none; } }
  @keyframes splashWordmarkIn { 0%, 100% { opacity: 1; transform: none; filter: none; } }
  @keyframes splashLetterIn { 0%, 100% { opacity: 1; transform: none; filter: none; } }
  @keyframes splashTaglineIn { 0%, 100% { opacity: 1; transform: none; } }
  @keyframes splashLoaderFadeIn { 0%, 100% { opacity: 1; } }
  @keyframes splashLoaderPulse { 0%, 100% { transform: translate(-50%, -50%) scale(1); } }
}
`;

interface SplashScreenProps {
  visible: boolean;
}

export default function SplashScreen({ visible }: SplashScreenProps) {
  const isDark = useMemo(detectDark, []);
  const reduced = useMemo(prefersReducedMotion, []);
  const p = isDark ? DARK : LIGHT;
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    document.getElementById("splash")?.remove();
  }, []);

  useEffect(() => {
    if (!visible && !removed) {
      const timer = setTimeout(() => setRemoved(true), 500);
      return () => clearTimeout(timer);
    }
  }, [visible, removed]);

  if (removed) return null;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: SPLASH_STYLES }} />
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 99999,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: p.bg,
          overflow: "hidden",
          transition: visible
            ? undefined
            : "opacity 400ms ease, transform 400ms ease",
          opacity: visible ? 1 : 0,
          transform: visible ? "none" : "translateY(-8px) scale(1.02)",
          pointerEvents: visible ? "auto" : "none",
          padding:
            "env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)",
        }}
      >
        {/* Aurora blobs */}
        {!reduced && (
          <>
            <div
              style={{
                position: "absolute",
                width: 600,
                height: 600,
                top: "-15%",
                right: "-10%",
                borderRadius: "50%",
                background: `radial-gradient(circle, ${p.aurora1}, ${p.aurora1b}, transparent 70%)`,
                filter: "blur(100px)",
                willChange: "transform",
                animation: "splashAurora1 14s ease-in-out infinite",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                width: 500,
                height: 500,
                bottom: "-10%",
                left: "-8%",
                borderRadius: "50%",
                background: `radial-gradient(circle, ${p.aurora2}, ${p.aurora2b}, transparent 70%)`,
                filter: "blur(100px)",
                willChange: "transform",
                animation: "splashAurora2 16s ease-in-out 1s infinite",
                pointerEvents: "none",
              }}
            />
          </>
        )}

        {/* Logo group — wordmark + FourSquare accent behind */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >



          {/* Layer 1: Gradient ACEX wordmark */}
          <div
            style={{
              display: "flex",
              gap: 4,
              position: "relative",
              zIndex: 1,
            }}
          >
            {LOGO_LETTERS.map((letter, i) => (
              <span
                key={letter}
                style={{
                  fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
                  fontSize: 56,
                  fontWeight: 800,
                  letterSpacing: -2,
                  lineHeight: 1,
                  background: p.logoGradient,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  opacity: reduced ? 1 : 0,
                  animation: reduced
                    ? "none"
                    : `splashLetterIn 500ms cubic-bezier(0.16,1,0.3,1) ${i * 120}ms forwards`,
                }}
              >
                {letter}
              </span>
            ))}
          </div>
        </div>

        {/* Layer 2: Tagline */}
        <p
          style={{
            position: "relative",
            zIndex: 1,
            color: p.tagline,
            fontSize: 14,
            fontFamily: "system-ui, sans-serif",
            margin: "8px 0 40px",
            letterSpacing: 1.5,
            textTransform: "uppercase",
            opacity: reduced ? 1 : 0,
            animation: reduced
              ? "none"
              : "splashTaglineIn 500ms ease-out 550ms forwards",
          }}
        >
          Trade Beyond Limits
        </p>
      </div>
    </>
  );
}
